import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function HomePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedService, setSelectedService] = useState('');

  // Handle scroll effect for navbar
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => {
      setIsScrolled(window.scrollY > 50);
    });
  }

  const handleServiceSelect = (serviceValue: string) => {
    setSelectedService(serviceValue);
    // Scroll to booking section
    const bookingSection = document.getElementById('booking');
    if (bookingSection) {
      bookingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <i className="ri-tools-fill text-3xl text-teal-600"></i>
              <span className={`text-2xl font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>Kyle Handyman Services</span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <a href="#home" className={`font-medium transition-colors ${isScrolled ? 'text-gray-900 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>Home</a>
              <a href="#services" className={`font-medium transition-colors ${isScrolled ? 'text-gray-900 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>Services</a>
              <a href="#booking" className={`font-medium transition-colors ${isScrolled ? 'text-gray-900 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>Book Appointment</a>
              <a href="#payment" className={`font-medium transition-colors ${isScrolled ? 'text-gray-900 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>Payment</a>
              <a href="#contact" className={`font-medium transition-colors ${isScrolled ? 'text-gray-900 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>Contact</a>
              <a href="#about" className={`font-medium transition-colors ${isScrolled ? 'text-gray-900 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>About Us</a>
              <a href="#booking" className="bg-teal-600 text-white px-6 py-2.5 rounded-full font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                Book Now
              </a>
            </div>

            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden">
              <i className={`ri-menu-line text-2xl ${isScrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white shadow-lg">
            <div className="px-6 py-4 space-y-3">
              <a href="#home" className="block text-gray-900 hover:text-teal-600 font-medium">Home</a>
              <a href="#services" className="block text-gray-900 hover:text-teal-600 font-medium">Services</a>
              <a href="#booking" className="block text-gray-900 hover:text-teal-600 font-medium">Book Appointment</a>
              <a href="#payment" className="block text-gray-900 hover:text-teal-600 font-medium">Payment</a>
              <a href="#contact" className="block text-gray-900 hover:text-teal-600 font-medium">Contact</a>
              <a href="#about" className="block text-gray-900 hover:text-teal-600 font-medium">About Us</a>
              <a href="#booking" className="block bg-teal-600 text-white px-6 py-2.5 rounded-full font-medium text-center cursor-pointer">Book Now</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center">
        <div className="absolute inset-0 w-full h-full">
          <img 
            src="https://public.readdy.ai/ai/img_res/0c1f6d86a4605df7e16cae36047adeb6.jpg" 
            alt="Professional Handyman Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <div className="inline-block mb-6 px-4 py-2 border border-white/30 rounded-full backdrop-blur-sm">
            <span className="text-white text-sm font-medium">Professional · Reliable · Fast Response</span>
          </div>
          <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Your Trusted Home<br />Repair Expert
          </h1>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Plumbing · Electrical · Furniture Assembly · Equipment Maintenance · One-Stop Solution
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#services" className="bg-teal-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-teal-700 transition-all hover:scale-105 whitespace-nowrap cursor-pointer">
              View Services
            </a>
            <a href="#about" className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-full font-semibold text-lg border-2 border-white/30 hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer">
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold mb-4">OUR SERVICES</span>
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Comprehensive Home Repair Services</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">From minor fixes to major installations, we handle all your home maintenance needs</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: 'ri-drop-line', title: 'Plumbing Services', desc: 'Leak repairs, pipe installation, drain cleaning, and water heater maintenance', value: 'plumbing', price: '$99+' },
              { icon: 'ri-lightbulb-flash-line', title: 'Electrical Work', desc: 'Wiring, outlet installation, lighting fixtures, and electrical troubleshooting', value: 'electrical', price: '$129+' },
              { icon: 'ri-hammer-line', title: 'Furniture Assembly', desc: 'Professional assembly of all types of furniture, shelving, and storage units', value: 'furniture', price: '$79+' },
              { icon: 'ri-settings-3-line', title: 'Appliance Repair', desc: 'Repair and maintenance of household appliances and equipment', value: 'appliance', price: '$149+' },
              { icon: 'ri-paint-brush-line', title: 'Painting Services', desc: 'Interior and exterior painting, wall preparation, and color consultation', value: 'painting', price: '$199+' },
              { icon: 'ri-door-line', title: 'Door & Window Repair', desc: 'Installation, repair, and maintenance of doors, windows, and locks', value: 'door-window', price: '$89+' },
              { icon: 'ri-flashlight-line', title: 'Lighting Installation', desc: 'Install and replace all types of indoor and outdoor lighting fixtures', value: 'lighting', price: '$69+' },
              { icon: 'ri-alarm-warning-line', title: 'Emergency Repairs', desc: '24/7 emergency service for urgent home repair situations', value: 'emergency', price: '$299+' }
            ].map((service, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-b-4 border-transparent hover:border-teal-600 group cursor-pointer">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${service.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed mb-4">{service.desc}</p>
                <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-100">
                  <div>
                    <span className="text-sm text-gray-500">Starting from</span>
                    <div className="text-2xl font-bold text-teal-600">{service.price}</div>
                  </div>
                  <button 
                    onClick={() => handleServiceSelect(service.value)}
                    className="bg-teal-600 text-white px-6 py-2.5 rounded-full font-medium hover:bg-teal-700 transition-colors whitespace-nowrap flex items-center space-x-2"
                  >
                    <span>Book Now</span>
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section id="booking" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-2">
              <span className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold mb-4">BOOK NOW</span>
              <h2 className="text-5xl font-bold text-gray-900 mb-8 leading-tight">Complete Your Booking in 3 Steps</h2>
              
              <div className="space-y-6 mb-8">
                {[
                  { num: '1', title: 'Select Service Type', desc: 'Choose from our comprehensive list of home repair services' },
                  { num: '2', title: 'Pick Date & Time', desc: 'Select a convenient time slot that works for your schedule' },
                  { num: '3', title: 'Confirm & Pay', desc: 'Review your booking details and complete secure payment' }
                ].map((step, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-xl">{step.num}</span>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-1">{step.title}</h4>
                      <p className="text-gray-600">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center space-x-3 text-gray-600">
                <i className="ri-shield-check-line text-2xl text-teal-600"></i>
                <span className="font-medium">Trusted by 10,000+ Families</span>
              </div>
            </div>

            <div className="lg:col-span-3">
              <BookingForm selectedService={selectedService} />
            </div>
          </div>
        </div>
      </section>

      {/* Payment Section */}
      <section id="payment" className="py-24 bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-teal-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-teal-400 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-teal-500/20 text-teal-300 rounded-full text-sm font-semibold mb-4">TRANSPARENT PRICING</span>
            <h2 className="text-5xl font-bold text-white mb-4">Clear Pricing · No Hidden Fees</h2>
            <p className="text-xl text-gray-300">Choose the service package that fits your needs</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
            {[
              { 
                name: 'Basic Service', 
                price: '99', 
                popular: false,
                features: ['Single repair task', 'Up to 1 hour service', 'Basic tools included', 'Same-day booking', 'Email support']
              },
              { 
                name: 'Recommended Package', 
                price: '299', 
                popular: true,
                features: ['Multiple repair tasks', 'Up to 3 hours service', 'All tools included', 'Priority scheduling', '24/7 phone support', 'Free follow-up visit']
              },
              { 
                name: 'Premium Service', 
                price: '599', 
                popular: false,
                features: ['Unlimited tasks', 'Full day service', 'Professional equipment', 'Immediate response', 'Dedicated support', '30-day warranty', 'Free maintenance tips']
              }
            ].map((plan, index) => (
              <div key={index} className={`bg-white rounded-3xl p-8 ${plan.popular ? 'transform scale-110 shadow-2xl' : 'shadow-lg'} transition-all hover:scale-105`}>
                {plan.popular && (
                  <div className="bg-gradient-to-r from-amber-400 to-amber-500 text-white text-sm font-bold px-4 py-2 rounded-full inline-block mb-4">
                    MOST POPULAR
                  </div>
                )}
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-teal-600">${plan.price}</span>
                  <span className="text-gray-600 ml-2">starting from</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <i className="ri-check-line text-teal-600 text-xl mr-3"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <a href="#booking" className={`block w-full py-3 rounded-full font-semibold text-center transition-colors whitespace-nowrap cursor-pointer ${
                  plan.popular 
                    ? 'bg-teal-600 text-white hover:bg-teal-700' 
                    : 'bg-white border-2 border-teal-600 text-teal-600 hover:bg-teal-50'
                }`}>
                  {plan.popular ? 'Book Now' : 'Select Service'}
                </a>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <p className="text-white text-lg mb-6">We Accept Multiple Payment Methods</p>
            <div className="flex items-center justify-center space-x-6">
              {/* Visa */}
              <div className="bg-white rounded-lg px-4 py-2 flex items-center justify-center h-12 w-20 shadow-md">
                <div className="text-blue-700 font-bold text-xl italic tracking-tight">VISA</div>
              </div>
              {/* Mastercard */}
              <div className="bg-white rounded-lg px-3 py-2 flex items-center justify-center h-12 w-20 shadow-md">
                <div className="flex items-center">
                  <div className="w-6 h-6 bg-red-500 rounded-full -mr-2"></div>
                  <div className="w-6 h-6 bg-amber-400 rounded-full opacity-90"></div>
                </div>
              </div>
              {/* American Express */}
              <div className="bg-blue-600 rounded-lg px-2 py-2 flex items-center justify-center h-12 w-20 shadow-md">
                <div className="text-white font-bold text-xs text-center leading-tight">AMERICAN<br/>EXPRESS</div>
              </div>
              {/* Discover */}
              <div className="bg-white rounded-lg px-2 py-2 flex items-center justify-center h-12 w-20 shadow-md">
                <div className="text-orange-500 font-bold text-sm tracking-tight">DISCOVER</div>
              </div>
              {/* PayPal */}
              <div className="bg-white rounded-lg px-3 py-2 flex items-center justify-center h-12 w-20 shadow-md">
                <div className="flex items-center">
                  <span className="text-blue-800 font-bold text-sm">Pay</span>
                  <span className="text-blue-500 font-bold text-sm">Pal</span>
                </div>
              </div>
              {/* Apple Pay */}
              <div className="bg-black rounded-lg px-3 py-2 flex items-center justify-center h-12 w-20 shadow-md">
                <div className="flex items-center space-x-1">
                  <i className="ri-apple-fill text-white text-lg"></i>
                  <span className="text-white font-medium text-sm">Pay</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-gradient-to-b from-orange-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 mb-12">
            <div className="lg:col-span-1">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-3 h-3 bg-teal-600 rounded-full"></div>
                <span className="text-gray-900 font-semibold">Customer Reviews</span>
              </div>
              <h2 className="text-5xl font-bold text-gray-900 mb-4 leading-tight">Real User<br />Feedback</h2>
              <p className="text-gray-600">From 1,000+ verified reviews</p>
            </div>

            <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { name: 'Sarah Johnson', rating: 5, text: 'Excellent service! The technician arrived on time and fixed my plumbing issue quickly. Very professional and courteous.' },
                { name: 'Michael Chen', rating: 5, text: 'I was impressed by the quality of work. They assembled all my furniture perfectly and cleaned up afterwards. Highly recommend!' },
                { name: 'Emily Rodriguez', rating: 5, text: 'Fast response time and fair pricing. The electrician was knowledgeable and explained everything clearly. Will use again!' },
                { name: 'David Thompson', rating: 5, text: 'Outstanding experience from booking to completion. The team was professional, efficient, and did a fantastic job.' },
                { name: 'Lisa Anderson', rating: 5, text: 'Very satisfied with the painting service. Attention to detail was excellent and they finished ahead of schedule.' },
                { name: 'James Wilson', rating: 5, text: 'Reliable and trustworthy service. They fixed my door lock issue and gave helpful maintenance tips. Great value!' }
              ].map((review, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-shadow">
                  <div className="flex space-x-1 mb-4">
                    {[...Array(review.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-amber-400 text-lg"></i>
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 leading-relaxed">{review.text}</p>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">{review.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{review.name}</p>
                      <i className="ri-checkbox-circle-fill text-green-500 text-sm"></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold mb-6">ABOUT US</span>
              <h2 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Professional Team<br />
                <span className="text-teal-600">Dedicated Service</span>
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                With over 10 years of experience in home repair and maintenance, our certified technicians are committed to providing top-quality service to every customer. We understand that your home is your most valuable asset, and we treat it with the care and respect it deserves.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our team consists of licensed professionals who undergo continuous training to stay updated with the latest techniques and safety standards. We pride ourselves on our reliability, transparency, and customer satisfaction.
              </p>

              <div className="grid grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="text-4xl font-bold text-teal-600 mb-2">10,000+</div>
                  <div className="text-sm text-gray-600">Families Served</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-teal-600 mb-2">98%</div>
                  <div className="text-sm text-gray-600">Satisfaction Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-teal-600 mb-2">2hrs</div>
                  <div className="text-sm text-gray-600">Response Time</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <a href="#services" className="bg-teal-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                  Learn More
                </a>
                <button className="bg-white border-2 border-gray-300 text-gray-900 px-8 py-3 rounded-full font-semibold hover:border-teal-600 hover:text-teal-600 transition-colors whitespace-nowrap">
                  View Certifications
                </button>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20handyman%20technician%20in%20uniform%20working%20on%20home%20repair%20with%20tools%2C%20friendly%20smile%2C%20modern%20clean%20workspace%2C%20high%20quality%20professional%20photography%20showing%20expertise%20and%20professionalism&width=800&height=1000&seq=about-handyman-002&orientation=portrait" 
                  alt="Professional Handyman Team"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute bottom-8 right-8 bg-white rounded-2xl p-6 shadow-xl">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center">
                    <i className="ri-shield-check-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Licensed & Insured</p>
                    <p className="text-sm text-gray-600">Certified Professionals</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-br from-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-5xl font-bold text-white mb-8">Always Here to Serve You</h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-lg mb-1">Phone</h4>
                    <p className="text-gray-300">719-741-5201</p>
                    <p className="text-gray-400 text-sm">Available 24/7</p>
                  </div>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-lg mb-1">Email</h4>
                    <p className="text-gray-300">service@kyle-handyman.com</p>
                    <p className="text-gray-400 text-sm">Response within 2 hours</p>
                  </div>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-lg mb-1">Address</h4>
                    <p className="text-gray-300">1140 S 101st E Ave</p>
                    <p className="text-gray-400 text-sm">Tulsa, OK 74128</p>
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <button className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-xl text-white"></i>
                </button>
                <button className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-twitter-fill text-xl text-white"></i>
                </button>
                <button className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-xl text-white"></i>
                </button>
                <button className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-xl text-white"></i>
                </button>
              </div>
            </div>

            <div>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 border-t-4 border-teal-600">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <i className="ri-tools-fill text-3xl text-teal-600"></i>
                <span className="text-2xl font-bold text-white">Kyle Handyman Services</span>
              </div>
              <p className="text-gray-400 mb-6">Professional Repair · Trusted Service</p>
              <p className="text-gray-400 leading-relaxed">Your reliable partner for all home maintenance and repair needs. Quality workmanship guaranteed.</p>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Services</h4>
              <ul className="space-y-2">
                <li><a href="#services" className="text-gray-400 hover:text-teal-600 transition-colors cursor-pointer">Plumbing</a></li>
                <li><a href="#services" className="text-gray-400 hover:text-teal-600 transition-colors cursor-pointer">Electrical</a></li>
                <li><a href="#services" className="text-gray-400 hover:text-teal-600 transition-colors cursor-pointer">Furniture Assembly</a></li>
                <li><a href="#services" className="text-gray-400 hover:text-teal-600 transition-colors cursor-pointer">Painting</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#about" className="text-gray-400 hover:text-teal-600 transition-colors cursor-pointer">About Us</a></li>
                <li><a href="#booking" className="text-gray-400 hover:text-teal-600 transition-colors cursor-pointer">Book Now</a></li>
                <li><a href="#payment" className="text-gray-400 hover:text-teal-600 transition-colors cursor-pointer">Pricing</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-teal-600 transition-colors cursor-pointer">Contact</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Working Hours</h4>
              <div className="text-3xl font-bold text-white mb-2">24/7</div>
              <p className="text-gray-400 mb-4">Available Year-Round</p>
              <div className="flex space-x-3">
                <button className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-white"></i>
                </button>
                <button className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-twitter-fill text-white"></i>
                </button>
                <button className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-white"></i>
                </button>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-gray-400 text-sm">© 2025 Kyle Handyman Services. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function BookingForm({ selectedService }: { selectedService: string }) {
  const [formData, setFormData] = useState({
    service: selectedService,
    date: '',
    timeSlot: '',
    name: '',
    phone: '',
    address: '',
    notes: ''
  });
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    cardName: '',
    expiry: '',
    cvv: '',
    paymentMethod: 'card'
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  // Update service when selectedService prop changes
  useEffect(() => {
    if (selectedService) {
      setFormData(prev => ({ ...prev, service: selectedService }));
    }
  }, [selectedService]);

  const servicePrices: { [key: string]: number } = {
    'plumbing': 99,
    'electrical': 129,
    'furniture': 79,
    'appliance': 149,
    'painting': 199,
    'door-window': 89,
    'lighting': 69,
    'emergency': 299
  };

  const serviceNames: { [key: string]: string } = {
    'plumbing': 'Plumbing Services',
    'electrical': 'Electrical Work',
    'furniture': 'Furniture Assembly',
    'appliance': 'Appliance Repair',
    'painting': 'Painting Services',
    'door-window': 'Door & Window Repair',
    'lighting': 'Lighting Installation',
    'emergency': 'Emergency Repairs'
  };

  const timeSlotNames: { [key: string]: string } = {
    'morning': 'Morning (8AM-12PM)',
    'afternoon': 'Afternoon (12PM-5PM)',
    'evening': 'Evening (5PM-8PM)'
  };

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    return parts.length ? parts.join(' ') : value;
  };

  const formatExpiry = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };

  const validateStep = (step: number) => {
    const newErrors: {[key: string]: string} = {};
    
    if (step === 1) {
      if (!formData.service) newErrors.service = 'Please select a service';
      if (!formData.date) newErrors.date = 'Please select a date';
      if (!formData.timeSlot) newErrors.timeSlot = 'Please select a time slot';
    } else if (step === 2) {
      if (!formData.name.trim()) newErrors.name = 'Please enter your name';
      if (!formData.phone.trim()) newErrors.phone = 'Please enter your phone number';
      if (formData.phone && !/^[\d\s\-+()]{10,}$/.test(formData.phone)) {
        newErrors.phone = 'Please enter a valid phone number';
      }
      if (!formData.address.trim()) newErrors.address = 'Please enter your address';
    } else if (step === 3 && paymentData.paymentMethod === 'card') {
      if (!paymentData.cardNumber.replace(/\s/g, '')) {
        newErrors.cardNumber = 'Please enter card number';
      } else if (paymentData.cardNumber.replace(/\s/g, '').length < 16) {
        newErrors.cardNumber = 'Please enter a valid card number';
      }
      if (!paymentData.cardName.trim()) newErrors.cardName = 'Please enter cardholder name';
      if (!paymentData.expiry) {
        newErrors.expiry = 'Please enter expiry date';
      } else if (!/^\d{2}\/\d{2}$/.test(paymentData.expiry)) {
        newErrors.expiry = 'Please enter valid expiry (MM/YY)';
      }
      if (!paymentData.cvv) {
        newErrors.cvv = 'Please enter CVV';
      } else if (paymentData.cvv.length < 3) {
        newErrors.cvv = 'Please enter valid CVV';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateStep(currentStep)) return;
    
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
      setErrors({});
    } else {
      // Process payment
      setIsProcessing(true);
      
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setIsProcessing(false);
      setShowSuccess(true);
      
      setTimeout(() => {
        setShowSuccess(false);
        setCurrentStep(1);
        setFormData({
          service: '',
          date: '',
          timeSlot: '',
          name: '',
          phone: '',
          address: '',
          notes: ''
        });
        setPaymentData({
          cardNumber: '',
          cardName: '',
          expiry: '',
          cvv: '',
          paymentMethod: 'card'
        });
      }, 4000);
    }
  };

  const getServicePrice = () => {
    return servicePrices[formData.service] || 0;
  };

  const getCardType = () => {
    const number = paymentData.cardNumber.replace(/\s/g, '');
    if (number.startsWith('4')) return 'visa';
    if (/^5[1-5]/.test(number) || /^2[2-7]/.test(number)) return 'mastercard';
    if (number.startsWith('3')) return 'amex';
    return null;
  };

  return (
    <div className="bg-white rounded-3xl shadow-2xl p-8 relative overflow-hidden">
      {/* Processing Overlay */}
      {isProcessing && (
        <div className="absolute inset-0 bg-white/95 z-20 flex flex-col items-center justify-center">
          <div className="w-16 h-16 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin mb-4"></div>
          <p className="text-lg font-semibold text-gray-900">Processing Payment...</p>
          <p className="text-sm text-gray-500 mt-2">Please do not close this window</p>
        </div>
      )}

      {/* Success Overlay */}
      {showSuccess && (
        <div className="absolute inset-0 bg-white z-20 flex flex-col items-center justify-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
            <i className="ri-check-line text-4xl text-green-600"></i>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h3>
          <p className="text-gray-600 text-center max-w-sm mb-4">
            Your booking has been confirmed. We've sent a confirmation to your phone.
          </p>
          <div className="bg-gray-50 rounded-xl p-4 text-center">
            <p className="text-sm text-gray-500">Booking Reference</p>
            <p className="text-xl font-bold text-teal-600">HP-{Date.now().toString().slice(-8)}</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-bold text-gray-900">Book Your Service</h3>
        <div className="flex items-center space-x-2">
          {[1, 2, 3].map((step) => (
            <div key={step} className="flex items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                  currentStep > step 
                    ? 'bg-green-500 text-white' 
                    : currentStep === step 
                      ? 'bg-teal-600 text-white' 
                      : 'bg-gray-200 text-gray-500'
                }`}
              >
                {currentStep > step ? <i className="ri-check-line"></i> : step}
              </div>
              {step < 3 && (
                <div className={`w-6 h-0.5 ${currentStep > step ? 'bg-green-500' : 'bg-gray-200'}`}></div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Labels */}
      <div className="flex justify-between mb-6 text-xs text-gray-500">
        <span className={currentStep >= 1 ? 'text-teal-600 font-medium' : ''}>Service & Time</span>
        <span className={currentStep >= 2 ? 'text-teal-600 font-medium' : ''}>Your Details</span>
        <span className={currentStep >= 3 ? 'text-teal-600 font-medium' : ''}>Payment</span>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {currentStep === 1 && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Service Type *</label>
              <select 
                value={formData.service}
                onChange={(e) => {
                  setFormData({...formData, service: e.target.value});
                  if (errors.service) setErrors({...errors, service: ''});
                }}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm ${
                  errors.service ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select a service</option>
                <option value="plumbing">Plumbing Services - $99+</option>
                <option value="electrical">Electrical Work - $129+</option>
                <option value="furniture">Furniture Assembly - $79+</option>
                <option value="appliance">Appliance Repair - $149+</option>
                <option value="painting">Painting Services - $199+</option>
                <option value="door-window">Door & Window Repair - $89+</option>
                <option value="lighting">Lighting Installation - $69+</option>
                <option value="emergency">Emergency Repairs - $299+</option>
              </select>
              {errors.service && <p className="text-red-500 text-xs mt-1">{errors.service}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date *</label>
                <input 
                  type="date"
                  value={formData.date}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => {
                    setFormData({...formData, date: e.target.value});
                    if (errors.date) setErrors({...errors, date: ''});
                  }}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm ${
                    errors.date ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.date && <p className="text-red-500 text-xs mt-1">{errors.date}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Time Slot *</label>
                <select 
                  value={formData.timeSlot}
                  onChange={(e) => {
                    setFormData({...formData, timeSlot: e.target.value});
                    if (errors.timeSlot) setErrors({...errors, timeSlot: ''});
                  }}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm ${
                    errors.timeSlot ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select time</option>
                  <option value="morning">Morning (8AM-12PM)</option>
                  <option value="afternoon">Afternoon (12PM-5PM)</option>
                  <option value="evening">Evening (5PM-8PM)</option>
                </select>
                {errors.timeSlot && <p className="text-red-500 text-xs mt-1">{errors.timeSlot}</p>}
              </div>
            </div>

            {formData.service && (
              <div className="bg-teal-50 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
                    <i className="ri-price-tag-3-line text-white text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Estimated Price</p>
                    <p className="font-bold text-teal-600">${getServicePrice()}+</p>
                  </div>
                </div>
                <span className="text-xs text-gray-500">Final price after inspection</span>
              </div>
            )}
          </>
        )}

        {currentStep === 2 && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
              <input 
                type="text"
                value={formData.name}
                onChange={(e) => {
                  setFormData({...formData, name: e.target.value});
                  if (errors.name) setErrors({...errors, name: ''});
                }}
                placeholder="Enter your name"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
              <input 
                type="tel"
                value={formData.phone}
                onChange={(e) => {
                  setFormData({...formData, phone: e.target.value});
                  if (errors.phone) setErrors({...errors, phone: ''});
                }}
                placeholder="Enter your phone number"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Service Address *</label>
              <textarea 
                value={formData.address}
                onChange={(e) => {
                  setFormData({...formData, address: e.target.value});
                  if (errors.address) setErrors({...errors, address: ''});
                }}
                placeholder="Enter your complete address"
                rows={3}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm resize-none ${
                  errors.address ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Additional Notes (Optional)</label>
              <textarea 
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Any specific requirements or details"
                rows={2}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm resize-none"
              />
              <p className="text-gray-400 text-xs mt-1">{formData.notes.length}/500 characters</p>
            </div>
          </>
        )}

        {currentStep === 3 && (
          <div className="space-y-6">
            {/* Order Summary */}
            <div className="bg-gray-50 rounded-xl p-5">
              <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-file-list-3-line mr-2 text-teal-600"></i>
                Booking Summary
              </h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Service:</span>
                  <span className="font-semibold text-gray-900">{serviceNames[formData.service]}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date:</span>
                  <span className="font-semibold text-gray-900">
                    {formData.date ? new Date(formData.date).toLocaleDateString('en-US', { 
                      weekday: 'short', 
                      month: 'short', 
                      day: 'numeric' 
                    }) : ''}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Time:</span>
                  <span className="font-semibold text-gray-900">{timeSlotNames[formData.timeSlot]}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-semibold text-gray-900">{formData.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Phone:</span>
                  <span className="font-semibold text-gray-900">{formData.phone}</span>
                </div>
                <div className="pt-2 border-t border-gray-200">
                  <span className="text-gray-600">Address:</span>
                  <p className="font-semibold text-gray-900 mt-1">{formData.address}</p>
                </div>
              </div>
            </div>

            {/* Payment Amount */}
            <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-xl p-5 text-white">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-teal-100 text-sm">Total Amount</p>
                  <p className="text-3xl font-bold">${getServicePrice()}</p>
                </div>
                <div className="text-right">
                  <p className="text-teal-100 text-xs">Starting price</p>
                  <p className="text-teal-100 text-xs">Final cost after inspection</p>
                </div>
              </div>
            </div>

            {/* Payment Method Selection */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Payment Method</h4>
              <div className="grid grid-cols-2 gap-3">
                <button 
                  type="button" 
                  onClick={() => setPaymentData({...paymentData, paymentMethod: 'card'})}
                  className={`border-2 rounded-xl p-4 flex items-center justify-center space-x-2 cursor-pointer transition-all ${
                    paymentData.paymentMethod === 'card' 
                      ? 'border-teal-600 bg-teal-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <i className={`ri-bank-card-line text-2xl ${paymentData.paymentMethod === 'card' ? 'text-teal-600' : 'text-gray-500'}`}></i>
                  <span className={`font-medium ${paymentData.paymentMethod === 'card' ? 'text-teal-600' : 'text-gray-600'}`}>Credit Card</span>
                </button>
                <button 
                  type="button" 
                  onClick={() => setPaymentData({...paymentData, paymentMethod: 'wallet'})}
                  className={`border-2 rounded-xl p-4 flex items-center justify-center space-x-2 cursor-pointer transition-all ${
                    paymentData.paymentMethod === 'wallet' 
                      ? 'border-teal-600 bg-teal-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <i className={`ri-wallet-3-line text-2xl ${paymentData.paymentMethod === 'wallet' ? 'text-teal-600' : 'text-gray-500'}`}></i>
                  <span className={`font-medium ${paymentData.paymentMethod === 'wallet' ? 'text-teal-600' : 'text-gray-600'}`}>Pay on Site</span>
                </button>
              </div>
            </div>

            {/* Credit Card Form */}
            {paymentData.paymentMethod === 'card' && (
              <div className="space-y-4 bg-gray-50 rounded-xl p-5">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-gray-900">Card Details</h4>
                  <div className="flex space-x-2">
                    <div className={`w-10 h-6 rounded flex items-center justify-center ${getCardType() === 'visa' ? 'bg-blue-600' : 'bg-gray-300'}`}>
                      <span className="text-white text-xs font-bold">VISA</span>
                    </div>
                    <div className={`w-10 h-6 rounded flex items-center justify-center ${getCardType() === 'mastercard' ? 'bg-red-500' : 'bg-gray-300'}`}>
                      <span className="text-white text-xs font-bold">MC</span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Card Number *</label>
                  <div className="relative">
                    <input 
                      type="text"
                      value={paymentData.cardNumber}
                      onChange={(e) => {
                        const formatted = formatCardNumber(e.target.value);
                        if (formatted.replace(/\s/g, '').length <= 16) {
                          setPaymentData({...paymentData, cardNumber: formatted});
                          if (errors.cardNumber) setErrors({...errors, cardNumber: ''});
                        }
                      }}
                      placeholder="1234 5678 9012 3456"
                      className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm ${
                        errors.cardNumber ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    <i className="ri-bank-card-line absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  </div>
                  {errors.cardNumber && <p className="text-red-500 text-xs mt-1">{errors.cardNumber}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Cardholder Name *</label>
                  <input 
                    type="text"
                    value={paymentData.cardName}
                    onChange={(e) => {
                      setPaymentData({...paymentData, cardName: e.target.value.toUpperCase()});
                      if (errors.cardName) setErrors({...errors, cardName: ''});
                    }}
                    placeholder="JOHN DOE"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm uppercase ${
                      errors.cardName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.cardName && <p className="text-red-500 text-xs mt-1">{errors.cardName}</p>}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Date *</label>
                    <input 
                      type="text"
                      value={paymentData.expiry}
                      onChange={(e) => {
                        const formatted = formatExpiry(e.target.value.replace('/', ''));
                        if (formatted.replace('/', '').length <= 4) {
                          setPaymentData({...paymentData, expiry: formatted});
                          if (errors.expiry) setErrors({...errors, expiry: ''});
                        }
                      }}
                      placeholder="MM/YY"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm ${
                        errors.expiry ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.expiry && <p className="text-red-500 text-xs mt-1">{errors.expiry}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CVV *</label>
                    <div className="relative">
                      <input 
                        type="password"
                        value={paymentData.cvv}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          if (value.length <= 4) {
                            setPaymentData({...paymentData, cvv: value});
                            if (errors.cvv) setErrors({...errors, cvv: ''});
                          }
                        }}
                        placeholder="•••"
                        className={`w-full px-4 py-3 pr-10 border rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm ${
                          errors.cvv ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      <i className="ri-question-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-help" title="3 or 4 digit security code on the back of your card"></i>
                    </div>
                    {errors.cvv && <p className="text-red-500 text-xs mt-1">{errors.cvv}</p>}
                  </div>
                </div>

                <div className="flex items-center space-x-2 text-xs text-gray-500 pt-2">
                  <i className="ri-lock-line text-green-600"></i>
                  <span>Your payment information is encrypted and secure</span>
                </div>
              </div>
            )}

            {/* Pay on Site Info */}
            {paymentData.paymentMethod === 'wallet' && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                <div className="flex items-start space-x-3">
                  <i className="ri-information-line text-amber-600 text-xl mt-0.5"></i>
                  <div>
                    <h4 className="font-semibold text-amber-800 mb-1">Pay on Site</h4>
                    <p className="text-sm text-amber-700">
                      You can pay in cash or card when our technician arrives. A $20 booking fee will be charged upon confirmation.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex space-x-3 pt-2">
          {currentStep > 1 && (
            <button 
              type="button"
              onClick={() => {
                setCurrentStep(currentStep - 1);
                setErrors({});
              }}
              className="flex-1 bg-gray-100 text-gray-700 py-4 rounded-xl font-semibold text-lg hover:bg-gray-200 transition-colors whitespace-nowrap flex items-center justify-center space-x-2"
            >
              <i className="ri-arrow-left-line"></i>
              <span>Back</span>
            </button>
          )}
          <button 
            type="submit"
            disabled={isProcessing}
            className="flex-1 bg-teal-600 text-white py-4 rounded-xl font-semibold text-lg hover:bg-teal-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {currentStep === 3 ? (
              <>
                <i className="ri-secure-payment-line"></i>
                <span>Pay ${getServicePrice()}</span>
              </>
            ) : (
              <>
                <span>Next Step</span>
                <i className="ri-arrow-right-line"></i>
              </>
            )}
          </button>
        </div>

        {/* Security Badge */}
        {currentStep === 3 && (
          <div className="flex items-center justify-center space-x-4 pt-4 border-t border-gray-100">
            <div className="flex items-center space-x-1 text-xs text-gray-500">
              <i className="ri-shield-check-line text-green-600"></i>
              <span>SSL Secured</span>
            </div>
            <div className="flex items-center space-x-1 text-xs text-gray-500">
              <i className="ri-lock-line text-green-600"></i>
              <span>256-bit Encryption</span>
            </div>
            <div className="flex items-center space-x-1 text-xs text-gray-500">
              <i className="ri-verified-badge-line text-green-600"></i>
              <span>PCI Compliant</span>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}

function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5ubufr0o41p73073dt0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setShowSuccess(true);
        setFormData({ name: '', phone: '', message: '' });
        setTimeout(() => setShowSuccess(false), 3000);
      } else {
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
      }
    } catch (error) {
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8" data-readdy-form>
      <h3 className="text-2xl font-bold text-white mb-6">Quick Message</h3>
      <form id="contact-form" onSubmit={handleSubmit} className="space-y-5">
        <div>
          <input 
            type="text"
            name="name"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            placeholder="Your Name"
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
            required
          />
        </div>

        <div>
          <input 
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            placeholder="Phone Number"
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
            required
          />
        </div>

        <div>
          <textarea 
            name="message"
            value={formData.message}
            onChange={(e) => setFormData({...formData, message: e.target.value})}
            placeholder="Your Message"
            rows={4}
            maxLength={500}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm resize-none"
            required
          />
          <p className="text-gray-400 text-xs mt-1">{formData.message.length}/500 characters</p>
        </div>

        <button 
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-teal-600 text-white py-3 rounded-xl font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </button>
      </form>

      {showSuccess && (
        <div className="mt-4 bg-green-500 text-white px-4 py-3 rounded-lg flex items-center space-x-2">
          <i className="ri-check-line text-xl"></i>
          <span>Message sent successfully!</span>
        </div>
      )}

      {showError && (
        <div className="mt-4 bg-red-500 text-white px-4 py-3 rounded-lg flex items-center space-x-2">
          <i className="ri-error-warning-line text-xl"></i>
          <span>Failed to send message. Please try again.</span>
        </div>
      )}
    </div>
  );
}
